import type { Editor } from '@tiptap/react';
import { compressImage } from '@/utils/image-utils';

/** True when the file is an image we can embed. */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Compress an image file and insert it into the editor as an `image` node.
 *
 * Matches the legacy Lexical behavior: the compressed image is embedded as a
 * base64 data-URL in the document (kept small via {@link compressImage}'s WebP
 * downscale). Real S3 upload is the documented future swap point in
 * `image-utils.ts` — when that lands, `compressImage` returns a CDN URL and the
 * `src` here becomes that URL with no change to this call site.
 *
 * No-op for non-image files so paste/drop handlers can pass everything through.
 */
export async function insertImageFile(editor: Editor, file: File): Promise<void> {
  if (!isImageFile(file)) return;
  const { dataURL } = await compressImage(file);
  editor.chain().focus().setImage({ src: dataURL }).run();
}

/** Insert every image file in a list (drop/paste of multiple images). */
export async function insertImageFiles(editor: Editor, files: Iterable<File>): Promise<void> {
  for (const file of files) {
    if (isImageFile(file)) {
      // Sequential so the nodes land in selection order.
      await insertImageFile(editor, file);
    }
  }
}
